import React, { useCallback, useEffect, useState } from "react";
import { MessagesSquare, Archive, ArchiveRestore, Inbox } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useConfirm } from "../context/ConfirmContext";

const CARD = "bg-white rounded-2xl border border-[#EBE9E4] shadow-[0_8px_30px_rgba(45,45,45,0.04)]";
const when = (d) => new Date(d).toLocaleString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });

export const PortalMessages = ({ onChange }) => {
  const confirm = useConfirm();
  const [archived, setArchived] = useState(false);
  const [data, setData] = useState(null);
  const load = useCallback(() => api.get(`/portal/conversation?archived=${archived}`).then(({ data }) => setData(data)).catch(() => setData({ messages: [] })), [archived]);
  useEffect(() => { load(); }, [load]);
  const act = async (fn, ok) => { try { await fn(); toast.success(ok); load(); onChange && onChange(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };
  const toggle = (m) => act(() => api.post(`/portal/messages/${m.id}/archive?archived=${!archived}`), archived ? "Moved back to your inbox" : "Message archived");
  const clearAll = async () => {
    if (!(await confirm({ title: "Clear your inbox?", description: "All messages move to Archived and are marked as read. You can still read or restore them any time.", confirmLabel: "Clear inbox" }))) return;
    act(() => api.post("/portal/messages/archive-all"), "Inbox cleared");
  };
  const msgs = data?.messages || [];
  return (
    <div className={`${CARD} p-6 md:p-8`} data-testid="portal-conversation">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-2">
          <MessagesSquare className="h-5 w-5" style={{ color: "var(--sj-primary)" }} />
          <h3 className="font-head text-xl font-semibold">Messages from the Church</h3>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex rounded-full border p-0.5" style={{ borderColor: "var(--sj-border)" }}>
            {[[false, "Inbox", Inbox], [true, `Archived${data?.archived_count ? ` (${data.archived_count})` : ""}`, Archive]].map(([v, l, I]) => (
              <button key={String(v)} data-testid={`messages-view-${v ? "archived" : "inbox"}`} onClick={() => setArchived(v)} className="inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-semibold" style={archived === v ? { background: "var(--sj-primary)", color: "#fff" } : { color: "var(--sj-text-2)" }}><I className="h-3.5 w-3.5" /> {l}</button>
            ))}
          </div>
          {!archived && msgs.length > 0 && <button data-testid="messages-clear-inbox" onClick={clearAll} className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold border hover:bg-[#EDF2F4]" style={{ borderColor: "var(--sj-border)", color: "var(--sj-text-2)" }}><Archive className="h-3.5 w-3.5" /> Clear inbox</button>}
        </div>
      </div>
      <div className="space-y-3 max-h-[460px] overflow-y-auto pr-1" data-testid="conversation-thread">
        {data && msgs.length === 0 && (
          <div className="text-center py-10 flex flex-col items-center gap-3" style={{ color: "var(--sj-text-3)" }} data-testid="messages-empty">
            <MessagesSquare className="h-10 w-10" /> {archived ? "No archived messages." : "Your inbox is clear. New messages from your church leaders will appear here."}
          </div>
        )}
        {msgs.map((m) => (
          <div key={m.id} data-testid={`msg-${m.id}`} className="flex items-start gap-2">
            <div className="max-w-[85%] min-w-0">
              <div className="text-xs mb-1" style={{ color: "var(--sj-text-3)" }}>{m.sender_name} · {when(m.created_at)}</div>
              {m.subject && <div className="font-semibold text-sm mb-0.5">{m.subject}</div>}
              <div className="rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap break-words" style={{ background: "var(--sj-peach)", borderTopLeftRadius: 4 }}>{m.body}</div>
            </div>
            <button data-testid={`msg-archive-${m.id}`} onClick={() => toggle(m)} title={archived ? "Move to inbox" : "Archive"} className="mt-5 p-2 rounded-full hover:bg-[#EDF2F4] shrink-0" style={{ color: "var(--sj-text-3)" }}>
              {archived ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};
